import { useSyncExternalStore } from 'react';
import {
  createInitialState,
  systemClock,
  undo as undoOp,
  type AppState,
  type Change,
  type Clock,
  type OpResult,
  type UndoEntry,
} from '@autoday/core';
import { diffState, emptyDevice, StaleStateError, type DeviceState, type OutboxOp, type StorageAdapter } from './storage.ts';
import { randomId } from '../platform.ts';

export class PersistError extends Error {
  constructor(cause: unknown) {
    super('Ändringen kunde inte sparas på enheten och har INTE genomförts. ' + (cause instanceof Error ? cause.message : ''));
    this.name = 'PersistError';
  }
}

export interface StoreSnapshot {
  ready: boolean;
  state: AppState;
  device: DeviceState;
  undoStack: UndoEntry[];
  storageKind: StorageAdapter['kind'] | 'none';
  loadError: string | null;
}

type Listener = () => void;

/** Antal omräkningar när en annan flik hunnit skriva emellan. */
const STALE_RETRIES = 5;

/**
 * Enda vägen att ändra appdata. Ordningen är alltid: beräkna (ren domän) → spara (en transaktion)
 * → först därefter visa. Misslyckas sparningen visas inget som lyckat.
 */
export class AppStore {
  private snap: StoreSnapshot;
  private listeners = new Set<Listener>();
  private queue: Promise<unknown> = Promise.resolve();
  onCommitted: Array<(next: AppState, change: Change | null) => void> = [];

  /** Kontoprofil: ändringar läggs i utkorgen för synk. Gästprofil: aldrig. */
  readonly syncEnabled: boolean;

  constructor(
    private storage: StorageAdapter,
    readonly clock: Clock = systemClock,
    opts: { syncEnabled?: boolean } = {},
  ) {
    this.syncEnabled = !!opts.syncEnabled;
    this.snap = { ready: false, state: createInitialState(), device: emptyDevice(''), undoStack: [], storageKind: storage.kind, loadError: null };
  }

  get snapshot(): StoreSnapshot {
    return this.snap;
  }
  get state(): AppState {
    return this.snap.state;
  }
  get device(): DeviceState {
    return this.snap.device;
  }
  get storageAdapter(): StorageAdapter {
    return this.storage;
  }

  subscribe = (l: Listener) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  private emit(patch: Partial<StoreSnapshot>) {
    this.snap = { ...this.snap, ...patch };
    for (const l of this.listeners) l();
  }

  async init(): Promise<void> {
    try {
      let state = await this.storage.load();
      if (!state) {
        state = createInitialState();
        await this.storage.persist(null, state);
      }
      const stored = await this.storage.loadDevice();
      // Äldre enhetstillstånd saknar nya fält – fyll i utan att skriva över befintliga.
      const device = stored ? { ...emptyDevice(stored.deviceId), ...stored } : emptyDevice(randomId('dev_'));
      if (!stored) await this.storage.saveDevice(device);
      this.emit({ ready: true, state, device });
      // En annan flik med samma profil har sparat: visa den aktuella datan här också.
      this.storage.onExternalChange?.((what) => {
        void this.serial(() => (what === 'state' ? this.reloadState() : this.reloadDevice())).catch(() => undefined);
      });
    } catch (e) {
      this.emit({ ready: true, loadError: e instanceof Error ? e.message : String(e) });
    }
  }

  /** Seriell kö – två snabba åtgärder kan aldrig skriva över varandras resultat. */
  private serial<T>(fn: () => Promise<T>): Promise<T> {
    const p = this.queue.then(fn, fn);
    this.queue = p.catch(() => undefined);
    return p;
  }

  /** Läser om appdata (och enhetens synkrevisioner) från lagringen – en annan flik har skrivit. */
  private async reloadState(): Promise<void> {
    const state = (await this.storage.load()) ?? createInitialState();
    await this.reloadDevice();
    this.emit({ state });
  }

  private async reloadDevice(): Promise<void> {
    const stored = await this.storage.loadDevice();
    if (stored) this.emit({ device: { ...emptyDevice(stored.deviceId), ...stored } });
  }

  /**
   * Sparar prev → next. En annan flik kan ha skrivit sedan vi läste: då har INGET sparats, datan läses
   * om och ändringen räknas om (`compute`) på den aktuella datan – aldrig en tyst överskrivning.
   */
  private async persistFresh<T>(compute: (prev: AppState) => { next: AppState; result: T } | null, importId?: string, fromRemote = false): Promise<{ prev: AppState; next: AppState; result: T } | null> {
    for (let attempt = 0; ; attempt++) {
      const prev = this.snap.state;
      const r = compute(prev);
      if (!r) return null;
      try {
        await this.storage.persist(prev, r.next, fromRemote ? [] : this.outboxFor(prev, r.next, importId));
        return { prev, next: r.next, result: r.result };
      } catch (e) {
        if (e instanceof StaleStateError && attempt < STALE_RETRIES) {
          await this.reloadState();
          continue;
        }
        throw new PersistError(e);
      }
    }
  }

  /** Kör en domänoperation. Returnerar resultatet efter att det sparats. */
  run(fn: (state: AppState) => OpResult): Promise<OpResult> {
    return this.serial(async () => {
      const seen: { duplicate?: OpResult } = {};
      const saved = await this.persistFresh((prev) => {
        const r = fn(prev);
        if (r.duplicate) {
          seen.duplicate = r;
          return null;
        }
        return { next: r.state, result: r };
      });
      if (!saved) return seen.duplicate!;
      const res = saved.result;
      this.emit({ state: res.state, undoStack: res.undo ? [...this.snap.undoStack.slice(-49), res.undo] : this.snap.undoStack });
      for (const cb of this.onCommitted) cb(res.state, res.change);
      return res;
    });
  }

  /**
   * För åtgärder som inte är OpResult (import, inställningar, återställning, fjärrändringar).
   * fromRemote: ändringen kom från servern och ska inte skickas tillbaka.
   */
  replace(fn: (state: AppState) => AppState, opts: { fromRemote?: boolean; importId?: string } = {}): Promise<AppState> {
    return this.serial(async () => {
      const saved = await this.persistFresh(
        (prev) => {
          const next = fn(prev);
          return next === prev ? null : { next, result: null };
        },
        opts.importId,
        !!opts.fromRemote,
      );
      if (!saved) return this.snap.state;
      const next = saved.next;
      this.emit({ state: next });
      for (const cb of this.onCommitted) cb(next, null);
      return next;
    });
  }

  /** Ångra senaste (eller angiven) operation som en enhet. */
  undo(opId?: string): Promise<string> {
    return this.serial(async () => {
      const stack = this.snap.undoStack;
      const idx = opId ? stack.findIndex((u) => u.opId === opId) : stack.length - 1;
      const entry = stack[idx];
      if (!entry) throw new Error('Inget att ångra.');
      const saved = (await this.persistFresh((prev) => {
        const r = undoOp(prev, entry, this.clock.now());
        return { next: r.state, result: r.change };
      }))!;
      const state = saved.next;
      const change = saved.result;
      this.emit({ state, undoStack: stack.filter((_, i) => i !== idx) });
      for (const cb of this.onCommitted) cb(state, change);
      return entry.label;
    });
  }

  async updateDevice(fn: (d: DeviceState) => DeviceState): Promise<DeviceState> {
    return this.serial(async () => {
      for (let attempt = 0; ; attempt++) {
        const next = fn(this.snap.device);
        try {
          await this.storage.saveDevice(next);
        } catch (e) {
          if (e instanceof StaleStateError && attempt < STALE_RETRIES) {
            await this.reloadDevice();
            continue;
          }
          throw e;
        }
        this.emit({ device: next });
        return next;
      }
    });
  }

  /** Utkorg för synk – bara när ett AutoDay-konto är anslutet. Byggs från tillståndsskillnaden. */
  private outboxFor(prev: AppState, next: AppState, importId?: string): OutboxOp[] {
    if (!this.syncEnabled) return [];
    const now = this.clock.now().toString();
    const d = diffState(prev, next);
    const ops: OutboxOp[] = [];
    const base = (entity: OutboxOp['entity'], id: string) => this.snap.device.serverRevisions[entity + ':' + id] ?? null;
    const push = (entity: OutboxOp['entity'], id: string, doc: unknown, deleted = false, imp?: string) =>
      ops.push({ opId: randomId('op_'), createdAt: now, entity, id, baseRevision: base(entity, id), doc, deleted, ...(imp ? { importId: imp } : {}) });
    for (const e of d.events.put) push('event', e.id, e, !!e.deleted);
    for (const id of d.events.del) push('event', id, null, true);
    for (const o of d.overlays.put) push('overlay', o.key, o);
    for (const id of d.overlays.del) push('overlay', id, null, true);
    for (const t of d.templates.put) push('template', t.id, t);
    for (const id of d.templates.del) push('template', id, null, true);
    for (const si of d.sourceItems.put) push('sourceItem', si.key, si, false, importId ?? si.lastSeenImportId);
    for (const id of d.sourceItems.del) push('sourceItem', id, null, true, importId);
    if (prev.sources !== next.sources) for (const src of Object.values(next.sources)) if (prev.sources[src.id] !== src) push('importSource', src.id, src);
    if (prev.settings !== next.settings || prev.calendars !== next.calendars || prev.categories !== next.categories) {
      push('meta', 'settings', { settings: next.settings, calendars: next.calendars, categories: next.categories });
    }
    return ops;
  }
}

let current: AppStore | null = null;
export function setStore(s: AppStore) {
  current = s;
}
export function getStore(): AppStore {
  if (!current) throw new Error('Store ej initierad');
  return current;
}

export function useStore(): StoreSnapshot {
  const s = getStore();
  return useSyncExternalStore(s.subscribe, () => s.snapshot);
}
